import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import type {
  AttachmentSearchResultDTO,
  MessageAttachmentDTO,
} from '@nora-monorepo/nora-api-client';
import {
  appLogger,
  useApiStore,
  useToastStore,
} from '@nora-monorepo/ui-shared';

import type { AttachmentSearchOptions } from '#nora-chat-ui/components/modals/files-modal/files-modal.types';

export const useFilesModalStore = defineStore('FilesModalStore', () => {
  const isOpen = ref(false);
  const limit = ref<number>(15);
  const isLoading = ref(false);
  const conversationId = ref<string | undefined>(undefined);
  const total = ref(0);
  const attachments = ref<MessageAttachmentDTO[]>([]);
  let activeSearch: AttachmentSearchOptions | undefined;

  const api = useApiStore();
  const toast = useToastStore();

  /**
   * Toggles the open state of the files modal.
   */
  const toggleOpen = (): void => {
    isOpen.value = !isOpen.value;
  };

  /**
   * Resets the state of the files modal store to its initial values.
   */
  const reset = (): void => {
    activeSearch = undefined;
    limit.value = 15;
    isLoading.value = false;
    conversationId.value = undefined;
    total.value = 0;
    attachments.value = [];
  };

  watch(isOpen, (open) => {
    if (!open) {
      reset();
    }
  }, { flush: 'sync' });

  /**
   * Fetches attachments for a specific conversation based on the provided search options.
   * @param conversationId
   * @param searchOptions
   */
  const getAttachmentsForConversation = async (
    conversationId: string,
    searchOptions: AttachmentSearchOptions,
  ): Promise<AttachmentSearchResultDTO> => {
    const response = await api.unwrap(
      api.conversation.getAttachmentsForConversation({
        conversationId,
        ...searchOptions,
      }),
    );

    if (!response.data) {
      throw new Error('Attachment search returned no data');
    }

    return response.data;
  };

  /**
   * Fetches attachments based on the provided search options.
   * @param searchOptions
   */
  const searchAttachments = async (
    searchOptions: AttachmentSearchOptions,
  ): Promise<AttachmentSearchResultDTO> => {
    const response = await api.unwrap(api.chatSearch.searchAttachments(searchOptions));

    if (!response.data) {
      throw new Error('Attachment search returned no data');
    }

    return response.data;
  };

  /**
   * Requests a page of attachments based on the provided search options.
   * @param searchOptions
   */
  const requestPage = async (
    searchOptions: AttachmentSearchOptions,
  ): Promise<AttachmentSearchResultDTO> => {
    if (conversationId.value) {
      return await getAttachmentsForConversation(conversationId.value, searchOptions);
    }
    else {
      return await searchAttachments(searchOptions);
    }
  };

  /**
   * Performs a search for attachments based on the provided query options.
   * @param queryOptions
   */
  const search = async (
    queryOptions: AttachmentSearchOptions,
  ): Promise<void> => {
    const searchOptions = {
      ...queryOptions,
      limit: queryOptions.limit ?? limit.value,
      skip: queryOptions.skip ?? 0,
    };
    activeSearch = searchOptions;
    limit.value = searchOptions.limit;
    isLoading.value = true;

    try {
      const page = await requestPage(searchOptions);

      if (activeSearch !== searchOptions) {
        return;
      }

      total.value = page.pagination.total;
      attachments.value = page.results;
    }
    catch (error) {
      if (activeSearch !== searchOptions) {
        return;
      }

      appLogger.error({
        message: 'Could not load chat attachments',
        context: {
          conversationId: conversationId.value,
        },
        sensitiveContext: { error },
      });
      toast.addError({ message: 'Could not load attachments' });

      total.value = 0;
      attachments.value = [];
    }
    finally {
      if (activeSearch === searchOptions) {
        isLoading.value = false;
      }
    }
  };

  /**
   * Opens the files modal and optionally sets the conversation ID.
   * @param newConversationId
   */
  const openModal = async (newConversationId?: string): Promise<void> => {
    reset();

    isOpen.value = true;
    conversationId.value = newConversationId;

    await search({
      query: '',
      limit: limit.value,
    });
  };

  /**
   * Opens the files modal and sets the conversation ID.
   * @param newConversationId
   */
  const setConversationIdAndOpenModal = async (newConversationId: string): Promise<void> => {
    await openModal(newConversationId);
  };

  /**
   * Updates the name of an attachment in the current list of attachments.
   * @param attachmentId
   * @param newName
   */
  const updateAttachmentName = (attachmentId: string, newName: string): void => {
    const attachmentIndex = attachments.value
      .map(attachment => attachment.id)
      .indexOf(attachmentId);

    if (attachmentIndex < 0) {
      return;
    }

    const attachment = attachments.value[attachmentIndex];

    if (!attachment) {
      return;
    }

    attachment.fileName = newName;
  };

  /** Refreshes the active page and count, including mutations outside the loaded page. */
  const refreshForConversation = async (changedConversationId: string): Promise<void> => {
    if (
      !isOpen.value
      || !activeSearch
      || (conversationId.value && conversationId.value !== changedConversationId)
    ) {
      return;
    }

    await search(activeSearch);
  };

  const deleteAttachment = async (
    changedConversationId: string,
    attachmentId: string,
  ): Promise<void> => {
    if (
      !isOpen.value
      || (conversationId.value && conversationId.value !== changedConversationId)
    ) {
      return;
    }

    attachments.value = attachments.value.filter(attachment => attachment.id !== attachmentId);
    await refreshForConversation(changedConversationId);
  };

  const deleteAttachmentsByFileId = async (
    changedConversationId: string,
    fileId: string,
  ): Promise<void> => {
    if (
      !isOpen.value
      || (conversationId.value && conversationId.value !== changedConversationId)
    ) {
      return;
    }

    attachments.value = attachments.value.filter(attachment => (
      attachment.fileId !== fileId
      || (attachment.message?.conversationId ?? conversationId.value) !== changedConversationId
    ));
    await refreshForConversation(changedConversationId);
  };

  return {
    isOpen,
    isLoading,
    limit,
    conversationId,
    total,
    attachments,
    toggleOpen,
    openModal,
    setConversationIdAndOpenModal,
    search,
    updateAttachmentName,
    refreshForConversation,
    deleteAttachment,
    deleteAttachmentsByFileId,
    reset,
  };
});
